import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { accountingApi } from '../api/client';
import type { SavedAccountCode, TenantExpectedPeriod, TransactionReport, UploadResult, XeroAccountCode } from '../types';
import AccountTransactionsModal from './AccountTransactionsModal';

interface AutomatedXeroPanelProps {
  activeTenantId: string | null;
  xeroConnected: boolean;
  onCalculateResult: (result: UploadResult) => void;
  // Best-effort estimate of Xero's next pending pay period for this tenant,
  // used to pre-fill the date range below (still fully editable — some
  // reconciliation runs legitimately need a custom range). Absent on a
  // tenant's first-ever run, when there's no history to infer a calendar
  // from — the calendar-month default below is used instead.
  expectedPeriod?: TenantExpectedPeriod | null;
}

// Format a local calendar date as YYYY-MM-DD. toISOString() converts to UTC,
// which shifts the date back a day in timezones ahead of UTC (e.g. Australia).
function toLocalIso(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Default date range: first → last day of the previous calendar month.
function defaultStartDate(): string {
  const now = new Date();
  return toLocalIso(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}
function defaultEndDate(): string {
  const now = new Date();
  return toLocalIso(new Date(now.getFullYear(), now.getMonth(), 0)); // day 0 = last day of previous month
}
function lastDayOfMonth(isoDate: string): string {
  const [y, m] = isoDate.split('-').map(Number);
  return toLocalIso(new Date(y, m, 0)); // day 0 of next month = last day of this month
}

// Calculate Super Payment depends on InvoicePolish's internal API for the
// Xero token on every call (deliberately never cached — see xero.ts), which
// occasionally returns a transient 5xx with no action on our end to fix it.
// A plain retry has been observed to succeed seconds later, so auto-retry
// on 5xx specifically — never on 4xx, which are real application errors
// (e.g. "no account codes saved") that retrying can't fix.
const MAX_CALCULATE_ATTEMPTS = 3;
const CALCULATE_RETRY_DELAY_MS = 1500;

function isRetryableUpstreamError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return typeof status === 'number' && status >= 500 && status < 600;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function AutomatedXeroPanel({
  activeTenantId,
  xeroConnected,
  onCalculateResult,
  expectedPeriod,
}: AutomatedXeroPanelProps) {
  // Account codes loaded from Xero (chart of accounts)
  const [xeroAccounts, setXeroAccounts] = useState<XeroAccountCode[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  // Saved account codes for the active org
  const [savedCodes, setSavedCodes] = useState<SavedAccountCode[]>([]);
  const [savedCodesLoading, setSavedCodesLoading] = useState(false);

  // Combobox state
  const [searchValue, setSearchValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<XeroAccountCode | null>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete confirmation
  const [pendingDelete, setPendingDelete] = useState<SavedAccountCode | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Date range
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  // Tracks whether the user has manually touched the date inputs, so the
  // expected-period pre-fill below never clobbers a value they've already
  // set — it's a smart default, not a lock.
  const datesEditedRef = useRef(false);

  // Calculation state
  const [calculating, setCalculating] = useState(false);
  const [calculateAttempt, setCalculateAttempt] = useState(0); // 0 = idle, N = on attempt N of MAX_CALCULATE_ATTEMPTS
  const [calculateError, setCalculateError] = useState<string | null>(null);

  // Report preview state
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<TransactionReport | null>(null);

  // Load both account lists whenever the active org changes
  const loadData = useCallback(async () => {
    if (!activeTenantId || !xeroConnected) {
      setXeroAccounts([]);
      setSavedCodes([]);
      setAccountsError(null);
      return;
    }
    // Clear stale data from the previous org immediately so the dropdown
    // never shows the wrong org's account codes while the new ones load.
    setXeroAccounts([]);
    setSavedCodes([]);
    setAccountsLoading(true);
    setSavedCodesLoading(true);
    setAccountsError(null);

    const [acctRes, savedRes] = await Promise.allSettled([
      accountingApi.getXeroAccounts(),
      accountingApi.getSavedAccountCodes(),
    ]);

    setAccountsLoading(false);
    setSavedCodesLoading(false);

    if (acctRes.status === 'fulfilled') {
      setXeroAccounts(acctRes.value.data.accounts);
    } else {
      const err = acctRes.reason as { response?: { data?: { error?: string } } };
      setAccountsError(
        err?.response?.data?.error ?? 'Failed to load account codes from Xero.'
      );
    }

    if (savedRes.status === 'fulfilled') {
      setSavedCodes(savedRes.value.data.accountCodes);
    }
    // Saved codes failing silently — not a blocking error
  }, [activeTenantId, xeroConnected]);

  useEffect(() => {
    setSelectedAccount(null);
    setSearchValue('');
    setSaveError(null);
    setCalculateError(null);
    datesEditedRef.current = false;
    loadData();
  }, [loadData]);

  // Pre-fill the date range from Xero's expected next pending period once
  // it's known, unless the user has already edited the dates themselves.
  useEffect(() => {
    if (datesEditedRef.current) return;
    if (!expectedPeriod?.payPeriodStartDate || !expectedPeriod.payPeriodEndDate) return;
    setStartDate(expectedPeriod.payPeriodStartDate);
    setEndDate(expectedPeriod.payPeriodEndDate);
  }, [expectedPeriod]);

  // Close combobox when clicking outside
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        // Restore display value to the currently selected account (or clear)
        if (selectedAccount) {
          setSearchValue(`${selectedAccount.Code} – ${selectedAccount.Name}`);
        } else {
          setSearchValue('');
        }
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selectedAccount]);

  // Filtered accounts for the dropdown list
  const filteredAccounts = useMemo(() => {
    if (!searchValue) return xeroAccounts;
    const q = searchValue.toLowerCase();
    return xeroAccounts.filter(
      (a) =>
        String(a.Code ?? '').toLowerCase().includes(q) ||
        String(a.Name ?? '').toLowerCase().includes(q)
    );
  }, [xeroAccounts, searchValue]);

  // Already-saved codes (to prevent re-adding)
  const savedCodeSet = useMemo(
    () => new Set(savedCodes.map((s) => s.accountCode)),
    [savedCodes]
  );

  function handleComboboxFocus() {
    setDropdownOpen(true);
    setSearchValue('');
  }

  function handleComboboxChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchValue(e.target.value);
    setDropdownOpen(true);
    setSelectedAccount(null); // clear selection when user types freely
  }

  function handleSelectAccount(account: XeroAccountCode) {
    setSelectedAccount(account);
    setSearchValue(`${account.Code} – ${account.Name}`);
    setDropdownOpen(false);
    setSaveError(null);
  }

  async function handleAdd() {
    if (!selectedAccount) return;
    if (savedCodeSet.has(selectedAccount.Code)) {
      setSaveError(`${selectedAccount.Code} is already in your saved list.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await accountingApi.saveAccountCode(
        selectedAccount.Code,
        selectedAccount.Name
      );
      if (!data.duplicate) {
        setSavedCodes((prev) => [...prev, data.accountCode]);
      }
      setSelectedAccount(null);
      setSearchValue('');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSaveError(msg ?? 'Failed to save account code. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await accountingApi.deleteAccountCode(pendingDelete.id);
      setSavedCodes((prev) => prev.filter((c) => c.id !== pendingDelete.id));
    } catch {
      // Silently ignore — the row will still be there; user can retry
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  }

  async function handleCalculate() {
    if (!startDate || !endDate || savedCodes.length === 0) return;
    setCalculating(true);
    setCalculateError(null);

    let lastErr: unknown = null;
    for (let attempt = 1; attempt <= MAX_CALCULATE_ATTEMPTS; attempt++) {
      setCalculateAttempt(attempt);
      try {
        const { data } = await accountingApi.calculateSuper(startDate, endDate);
        setCalculating(false);
        setCalculateAttempt(0);
        onCalculateResult(data);
        return;
      } catch (err) {
        lastErr = err;
        if (!isRetryableUpstreamError(err) || attempt === MAX_CALCULATE_ATTEMPTS) break;
        await sleep(CALCULATE_RETRY_DELAY_MS * attempt);
      }
    }

    setCalculating(false);
    setCalculateAttempt(0);
    const msg = (lastErr as { response?: { data?: { error?: string } } })?.response?.data?.error;
    const note = isRetryableUpstreamError(lastErr)
      ? ` This looks like a temporary issue reaching InvoicePolish — auto-retried ${MAX_CALCULATE_ATTEMPTS} times. Please try again in a moment.`
      : '';
    setCalculateError((msg ?? 'Failed to calculate super. Check your date range and try again.') + note);
  }

  async function handlePreviewReport() {
    if (!startDate || !endDate || savedCodes.length === 0) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const { data } = await accountingApi.previewReport(startDate, endDate);
      setReportData(data.report);
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setReportError(msg ?? 'Failed to load report. Check your date range and try again.');
    } finally {
      setReportLoading(false);
    }
  }

  const canCalculate =
    savedCodes.length > 0 && !!startDate && !!endDate && startDate <= endDate && !calculating;

  const canPreview =
    savedCodes.length > 0 && !!startDate && !!endDate && startDate <= endDate && !reportLoading;

  // ── Not connected state ──────────────────────────────────────────────────
  if (!xeroConnected) {
    return (
      <div className="flex h-full min-h-44 flex-col items-center justify-center rounded-lg border border-slate-300 bg-white px-8 py-8 text-center shadow-sm">
        <p className="text-sm font-semibold text-slate-700">Automated Xero Report</p>
        <p className="mt-1 text-xs text-slate-500">Connect a Xero organisation to use this workflow.</p>
      </div>
    );
  }

  // ── Main panel ───────────────────────────────────────────────────────────
  return (
    <>
      <div className="flex h-full min-h-44 flex-col rounded-lg border border-slate-300 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-slate-200 px-5 py-4">
          <p className="text-sm font-semibold text-slate-900">Automated Xero Report</p>
          <p className="mt-0.5 text-xs text-slate-500">
            Select account codes, choose a date range, and pull data directly from Xero.
          </p>
        </div>

        <div className="flex flex-1 flex-col gap-5 px-5 py-5">
          {/* Account code selector row */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-700">
              Account code
            </label>
            <div className="flex gap-2">
              {/* Combobox */}
              <div ref={comboboxRef} className="relative flex-1">
                <input
                  type="text"
                  value={searchValue}
                  onFocus={handleComboboxFocus}
                  onChange={handleComboboxChange}
                  onKeyDown={(e) => e.key === 'Escape' && setDropdownOpen(false)}
                  placeholder={
                    accountsLoading
                      ? 'Loading account codes…'
                      : xeroAccounts.length === 0
                      ? 'No accounts found'
                      : 'Search by code or name…'
                  }
                  disabled={accountsLoading || xeroAccounts.length === 0}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100 disabled:bg-slate-50 disabled:text-slate-400"
                />
                {dropdownOpen && filteredAccounts.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                    {filteredAccounts.map((account) => {
                      const alreadySaved = savedCodeSet.has(account.Code);
                      return (
                        <button
                          key={account.Code}
                          type="button"
                          onClick={() => handleSelectAccount(account)}
                          className={[
                            'flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors',
                            alreadySaved
                              ? 'text-slate-400 cursor-default'
                              : 'text-slate-800 hover:bg-slate-50',
                          ].join(' ')}
                        >
                          <span className="font-mono font-semibold text-slate-600 w-12 shrink-0">
                            {account.Code}
                          </span>
                          <span className="truncate">{account.Name}</span>
                          {alreadySaved && (
                            <span className="ml-auto shrink-0 text-[10px] text-slate-400">saved</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Add button */}
              <button
                type="button"
                onClick={handleAdd}
                disabled={!selectedAccount || saving || savedCodeSet.has(selectedAccount?.Code ?? '')}
                title="Add account code"
                className="flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-md border border-slate-300 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saving ? (
                  <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </button>
            </div>

            {accountsError && (
              <div className="mt-2 flex items-start gap-2">
                <p className="text-xs text-red-600">{accountsError}</p>
                <button
                  type="button"
                  onClick={loadData}
                  className="shrink-0 text-xs text-slate-500 underline"
                >
                  Retry
                </button>
              </div>
            )}
            {saveError && (
              <p className="mt-1.5 text-xs text-red-600">{saveError}</p>
            )}
          </div>

          {/* Saved account codes list */}
          <div className="flex-1">
            <p className="mb-1.5 text-xs font-medium text-slate-700">
              Saved account codes
              {savedCodes.length > 0 && (
                <span className="ml-1 text-slate-400">({savedCodes.length})</span>
              )}
            </p>

            {savedCodesLoading ? (
              <div className="flex items-center gap-2 py-2 text-xs text-slate-400">
                <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading…
              </div>
            ) : savedCodes.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-200 px-4 py-3 text-center">
                <p className="text-xs text-slate-400">No account codes saved for this organisation.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
                {savedCodes.map((saved) => (
                  <li
                    key={saved.id}
                    className="flex items-center gap-3 px-3 py-2 text-xs"
                  >
                    <span className="font-mono font-semibold text-slate-600 w-12 shrink-0">
                      {saved.accountCode}
                    </span>
                    <span className="flex-1 truncate text-slate-700">
                      {saved.accountName ?? '—'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(saved)}
                      title="Remove account code"
                      className="shrink-0 text-slate-400 transition-colors hover:text-red-500"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Date range */}
          <div>
            <p className="mb-1.5 text-xs font-medium text-slate-700">
              Date range
              {expectedPeriod?.payPeriodStartDate === startDate && expectedPeriod?.payPeriodEndDate === endDate && (
                <span className="ml-1.5 font-normal text-slate-400">(matches Xero's next pending pay period)</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const val = e.target.value;
                  datesEditedRef.current = true;
                  setStartDate(val);
                  if (val) setEndDate(lastDayOfMonth(val));
                }}
                className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
              <span className="shrink-0 text-xs text-slate-400">to</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => {
                  datesEditedRef.current = true;
                  setEndDate(e.target.value);
                }}
                className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-100"
              />
            </div>
            {startDate && endDate && startDate > endDate && (
              <p className="mt-1 text-xs text-red-600">Start date must be before end date.</p>
            )}
          </div>

          {/* Report/calculate errors */}
          {reportError && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
              <p className="text-xs text-slate-700">{reportError}</p>
            </div>
          )}
          {calculateError && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
              <p className="text-xs text-slate-700">{calculateError}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {/* Report Download — coming soon */}
            <button
              type="button"
              disabled
              title="PDF download coming soon"
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-400 shadow-sm cursor-not-allowed opacity-60 select-none"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Report Download
            </button>

            {/* Report Popup */}
            <button
              type="button"
              onClick={handlePreviewReport}
              disabled={!canPreview}
              className="flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {reportLoading ? (
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
              {reportLoading ? 'Loading…' : 'Report Popup'}
            </button>

            {/* Calculate Super Payment */}
            <button
              type="button"
              onClick={handleCalculate}
              disabled={!canCalculate}
              className="btn-primary text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {calculating && (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {calculating
                ? calculateAttempt > 1
                  ? `Retrying… (${calculateAttempt}/${MAX_CALCULATE_ATTEMPTS})`
                  : 'Calculating…'
                : 'Calculate Super Payment'}
            </button>
          </div>
        </div>
      </div>

      {/* Account Transactions report modal */}
      {reportData && (
        <AccountTransactionsModal
          report={reportData}
          onClose={() => setReportData(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-6"
          onClick={() => !deleting && setPendingDelete(null)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-slate-900">Remove account code?</h3>
            <p className="mt-2 text-xs text-slate-600">
              This will remove{' '}
              <span className="font-mono font-semibold">
                {pendingDelete.accountCode}
              </span>
              {pendingDelete.accountName ? ` (${pendingDelete.accountName})` : ''} from your
              saved list for this organisation. Previously calculated reports are not affected.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
                className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {deleting && (
                  <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
